<template>
  <div class="page-shell">
    <v-row v-if="isLoading" dense>
      <v-col cols="12">
        <v-skeleton-loader class="hero-card" type="article" />
      </v-col>
      <v-col cols="12">
        <v-skeleton-loader class="section-card" type="article" />
      </v-col>
      <v-col cols="12">
        <v-skeleton-loader class="section-card" type="article, list-item-two-line, list-item-two-line, list-item-two-line" />
      </v-col>
      <v-col cols="12">
        <v-skeleton-loader class="section-card" type="article, list-item-two-line, list-item-two-line, list-item-two-line, list-item-two-line" />
      </v-col>
    </v-row>

    <template v-else-if="errorMessage">
      <AppCard class="section-card">
        <v-alert type="error" variant="tonal">
          {{ errorMessage }}
        </v-alert>
      </AppCard>
    </template>

    <template v-else-if="detail">
      <SubscriptionDetailHeroCard :detail="detail" />
      <SectionVendorCard :vendor="detail.vendor" />
      <SubscriptionDetailBillingCard :detail="detail" />
      <SubscriptionDetailAppointmentsCard v-if="detail.appointments?.length" :detail="detail" />
      <SubscriptionDetailPaymentHistoryCard v-if="detail.payments?.length" :detail="detail" />

      <AppCard v-if="detail.recurring_subscription?.metadata" class="section-card">
        <AppSectionHeader title="Metadata" />
        <v-divider />

        <pre class="metadata-block">{{ prettyMetadata }}</pre>
      </AppCard>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import AppCard from "@/shared/components/ui/AppCard.vue";
import AppSectionHeader from "@/shared/components/ui/AppSectionHeader.vue";
import { fetchCustomerSubscriptionDetail } from "@/shared/services/customers.api";
import SubscriptionDetailHeroCard from "@/modules/customer/components/SubscriptionDetailHeroCard.vue";
import SectionVendorCard from "@/modules/shared/components/SectionVendorCard.vue";
import SubscriptionDetailBillingCard from "@/modules/customer/components/SubscriptionDetailBillingCard.vue";
import SubscriptionDetailAppointmentsCard from "@/modules/customer/components/SubscriptionDetailAppointmentsCard.vue";
import SubscriptionDetailPaymentHistoryCard from "@/modules/customer/components/SubscriptionDetailPaymentHistoryCard.vue";

type DetailStatus = "Active" | "Paused" | "Inactive" | string;

type SubscriptionDetailResponse = {
  recurring_subscription: {
    id: string | number;
    status: DetailStatus;
    billing_interval: string | null;
    interval_count: number | null;
    start_date: string | null;
    end_date: string | null;
    next_billing_date: string | null;
    metadata: unknown;
  };
  package: {
    id: string | number | null;
    name: string | null;
    package_type: string | null;
    highlight: string | null;
    features: string[] | null;
  };
  plan: {
    id: string | number | null;
    amount: number | string | null;
    type_name: string | null;
    currency: string | null;
  };
  vendor: {
    id: string | number | null;
    name: string | null;
    logo: string | null;
    company_name: string | null;
    phone: string | null;
    address: string | null;
  };
  appointments: {
    id: string | number;
    appointment_no: string | null;
    appointment_date: string | null;
    appointment_time: string | null;
    status: string | null;
  }[];
  payments: {
    amount: number | string | null;
    status: string | null;
    card_brand: string | null;
    card_last4: string | null;
    paid_at: string | null;
  }[];
};

const route = useRoute();
const isLoading = ref(false);
const errorMessage = ref("");
const detail = ref<SubscriptionDetailResponse | null>(null);

const prettyMetadata = computed(() =>
  JSON.stringify(detail.value?.recurring_subscription?.metadata ?? {}, null, 2),
);

async function loadDetail(id: string | number) {
  isLoading.value = true;
  errorMessage.value = "";
  detail.value = null;

  try {
    const response: any = await fetchCustomerSubscriptionDetail(id);
    detail.value = response?.data ?? response ?? null;
  } catch (error) {
    console.error("Failed to load subscription detail", error);
    errorMessage.value = "Unable to load subscription details.";
  } finally {
    isLoading.value = false;
  }
}

watch(
  () => route.params.id,
  (id) => {
    if (id != null && id !== "") {
      void loadDetail(id.toString());
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.page-shell {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px;
}

.section-card {
  background: #fff;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.metadata-block {
  margin: 0;
  padding: 12px;
  border-radius: 12px;
  background: #f8fafc;
  overflow: auto;
  font-size: 12px;
  line-height: 1.5;
}
</style>
